import React, { useState } from 'react';
import Modal from '@jbhi-fi/lanyard-ui/components/Modal';
import { CouponModalContent } from './CouponModalContent';
import { Coupon, CouponOffering } from '../types';
import { PerksContainer } from '../PerksContainer';
import { styles } from './CouponModal.css';
import { Confetti } from '@jbhi-fi/lanyard-ui/components/Confetti';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { WalletQRModal } from '../Account/JBWallet/WalletQRModal';
import { CouponWalletContent } from './constants';

type CouponModalCoupon = Omit<Coupon, 'couponReference' | 'allocatedDate' | 'redemptionDate'>;
export type AddToWalletUrls = {
  appleLink?: string;
  googleLink?: string;
};

type CouponModalProps = {
  isOpen: boolean;
  coupon: CouponModalCoupon;
  isLoading: boolean;
  playCouponAppliedAnimation?: boolean;
  couponOffering?: CouponOffering;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
  onClose: () => void;
  getCouponWalletContent: ({
    couponCode,
    shouldGenerateQrCode,
    platform,
  }: {
    couponCode?: string;
    shouldGenerateQrCode?: boolean;
    platform?: 'ios' | 'android';
  }) => Promise<CouponWalletContent>;
  shouldShowAddToWalletButtons?: boolean;
};

const getRedemptionCondition = (coupon: CouponModalCoupon) => {
  if (coupon.canBeRedeemedInStore && !coupon.canBeRedeemedOnline) {
    return 'instore';
  }
  if (coupon.canBeRedeemedOnline && !coupon.canBeRedeemedInStore) {
    return 'online';
  }
  return undefined;
};

export const CouponModal = ({
  isOpen,
  coupon,
  isLoading,
  playCouponAppliedAnimation,
  couponOffering,
  onApplyCoupon,
  onRemoveCoupon,
  onClose,
  getCouponWalletContent,
  shouldShowAddToWalletButtons,
}: CouponModalProps) => {
  const isMobile = useBreakpoint('md', 'down');
  const redemptionCondition = getRedemptionCondition(coupon);
  const [qrModalContent, setQrModalContent] = useState<{ qrCode?: string; isOpen: boolean } | null>(null);
  const showModal = isOpen && !qrModalContent?.isOpen;

  return (
    <>
      {shouldShowAddToWalletButtons && qrModalContent?.qrCode ? (
        <WalletQRModal
          isOpen={qrModalContent?.isOpen ?? false}
          onClose={() => setQrModalContent(null)}
          qrCode={qrModalContent?.qrCode || ''}
        />
      ) : null}
      <Modal show={showModal} onClose={onClose} showFrame={true} frame={PerksContainer} customClass={styles.modal}>
        {playCouponAppliedAnimation ? (
          <Confetti
            triggerConfetti={playCouponAppliedAnimation}
            onComplete={onClose}
            origin={isMobile ? 'screenTop' : 'containerTop'}
          />
        ) : null}
        <CouponModalContent
          {...coupon}
          {...coupon.content}
          couponOffering={couponOffering}
          isLoading={isLoading}
          isApplied={coupon.isApplied}
          expiryDate={coupon.content?.expiryDateFormatted}
          termsAndConditions={coupon.content?.termsAndConditions}
          barcode={coupon.content?.barcode}
          redemptionCondition={redemptionCondition}
          onApplyCoupon={onApplyCoupon}
          onRemoveCoupon={onRemoveCoupon}
          setQrModalContent={setQrModalContent}
          getCouponWalletContent={getCouponWalletContent}
          shouldShowAddToWalletButtons={shouldShowAddToWalletButtons}
        />
      </Modal>
    </>
  );
};
